import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { apiErrorMessage } from "@/shared/utils/apiError"

interface ShowApiErrorOptions {
  title?: string
}

interface ErrorDialogContextValue {
  showApiError: (err: unknown, options?: ShowApiErrorOptions) => void
}

const ErrorDialogContext = createContext<ErrorDialogContextValue | null>(null)

interface DialogState {
  title: string
  message: string
}

const FALLBACK_TITLE = "Något gick fel"
const FALLBACK_MESSAGE = "Ett oväntat fel inträffade. Försök igen."

export function ErrorDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null)

  const showApiError = useCallback((err: unknown, options?: ShowApiErrorOptions) => {
    setState({
      title: options?.title ?? FALLBACK_TITLE,
      message: apiErrorMessage(err) ?? FALLBACK_MESSAGE,
    })
  }, [])

  const value = useMemo(() => ({ showApiError }), [showApiError])

  return (
    <ErrorDialogContext.Provider value={value}>
      {children}
      <Dialog open={state !== null} onOpenChange={(open) => !open && setState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{state?.title ?? FALLBACK_TITLE}</DialogTitle>
            <DialogDescription>{state?.message ?? FALLBACK_MESSAGE}</DialogDescription>
          </DialogHeader>
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </ErrorDialogContext.Provider>
  )
}

export function useErrorDialog() {
  const context = useContext(ErrorDialogContext)
  if (!context) {
    throw new Error("useErrorDialog must be used within an ErrorDialogProvider")
  }
  return context
}
